import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import {
  QUESTION_GROUP_LOGIC,
  QUESTION_GROUP_LOGIC_BRANCHES,
  QUESTION_TYPES,
} from '@api/instant_survey';

import LogicOptions from '../../../../../../../../../QuestionLogic/components/LogicOptions';
import QuestionLogicOptions from '../../../../../../../../../QuestionLogic/components/QuestionLogicOptions';
import {
  answerOperators,
  availableLogicOptions,
  instantSurveyGroupRefUrlVariable,
  LOGIC_OPTIONS,
} from '../../../../../../../../../QuestionLogic/utilities/constants';
import { SurveyContext } from '../../../../../../../../../../../../utilities/context';

import styles from './_index.module.scss';

const GroupLogicRow = ({
  branch,
  firstErrorRef,
  groupId,
  index,
  isCompleted,
  questionIndex,
  questionList,
  questionLogic,
  setQuestionLogic,
}) => {
  const { survey } = useContext(SurveyContext);

  const getLogicRows = (logic) => logic[QUESTION_GROUP_LOGIC][groupId][branch].logic;
  const logicRow = getLogicRows(questionLogic)[index];

  const getLogicType = () => {
    return logicRow?.url_variable === instantSurveyGroupRefUrlVariable ||
      (questionIndex === 0 && !isCompleted)
      ? LOGIC_OPTIONS.QUOTA_GROUP.value
      : LOGIC_OPTIONS.ANSWER.value;
  };

  const [logicType, setLogicType] = useState(getLogicType);

  useEffect(() => {
    setLogicType(getLogicType());
  }, [
    branch,
    index,
    instantSurveyGroupRefUrlVariable,
    isCompleted,
    groupId,
    questionLogic,
    questionIndex,
  ]);

  const filteredQuestionList = questionList.filter(
    (question) =>
      question.type !== QUESTION_TYPES.INSTRUCTIONS.value &&
      question.type !== QUESTION_TYPES.NUMERIC.value
  );
  const quotaGroupOptions = survey.groups
    .filter(({ id }) => id !== null && id !== undefined)
    .map((group) => ({
      id: group.id,
      text: group.name,
    }));
  const isQuotaGroupSelectionValid = () => {
    const groupIds = quotaGroupOptions.map(({ id }) => String(id));
    const logicQuotaGroupIds = (logicRow?.values || []).map(String);
    return logicQuotaGroupIds.every((id) => groupIds.includes(id));
  };

  const updateAnswers = (answers) => {
    const updatedLogic = cloneDeep(questionLogic);
    const row = getLogicRows(updatedLogic)[index];
    row.answers = answers;
    row.hasAnswerError = false;
    row.isFirstAnswerError = false;
    setQuestionLogic(updatedLogic);
  };

  const updateLogicType = (newLogicType) => {
    setLogicType(newLogicType);
    const updatedLogic = cloneDeep(questionLogic);
    const rows = getLogicRows(updatedLogic);
    if (newLogicType === LOGIC_OPTIONS.QUOTA_GROUP.value) {
      rows[index] = {
        url_variable: instantSurveyGroupRefUrlVariable,
        values: [],
      };
    } else {
      rows[index] = {
        answers: [],
        question: null,
      };
    }
    rows[index].operator = answerOperators[0];
    rows[index].hasAnswerError = false;
    setQuestionLogic(updatedLogic);
  };

  const updateOperator = (operator) => {
    const updatedLogic = cloneDeep(questionLogic);
    getLogicRows(updatedLogic)[index].operator = operator;
    setQuestionLogic(updatedLogic);
  };

  const updateQuestion = (question) => {
    const updatedLogic = cloneDeep(questionLogic);
    const row = getLogicRows(updatedLogic)[index];
    delete row.url_variable;
    delete row.values;
    row.answers = [];
    row.question = question;
    row.hasQuestionError = false;
    row.isFirstQuestionError = false;
    row.operator = answerOperators[0];

    setQuestionLogic(updatedLogic);
  };

  const updateQuotaGroups = (groups) => {
    const updatedLogic = cloneDeep(questionLogic);
    const row = getLogicRows(updatedLogic)[index];
    const groupIds = groups.map(({ id }) => String(id));
    delete row.answers;
    delete row.question;
    delete row.hasQuestionError;
    delete row.isFirstQuestionError;
    row.hasAnswerError = false;
    row.isFirstAnswerError = false;
    row.url_variable = instantSurveyGroupRefUrlVariable;
    row.values = groupIds;
    setQuestionLogic(updatedLogic);
  };

  return (
    <div className={styles['group-logic-row']}>
      <span>
        The{' '}
        <LogicOptions
          isCompleted={isCompleted}
          logicOptions={
            isCompleted || questionIndex !== 0
              ? availableLogicOptions
              : availableLogicOptions.slice(1)
          }
          optionsLabel="Type"
          setValue={updateLogicType}
          value={logicType}
        />
      </span>
      {logicType === LOGIC_OPTIONS.ANSWER.value ? (
        <>
          <span>&nbsp;to&nbsp;</span>
          <QuestionLogicOptions
            firstErrorRef={firstErrorRef}
            isCompleted={isCompleted}
            isError={logicRow.hasQuestionError}
            isFocused={logicRow.isFirstQuestionError}
            isQuestion
            isRequired
            options={filteredQuestionList}
            optionsLabel="Question"
            optionsType="radio"
            setValue={updateQuestion}
            value={logicRow.question}
          />
          {logicRow.question?.id ? (
            <>
              <span>&nbsp;is&nbsp;</span>
              <QuestionLogicOptions
                isCompleted={isCompleted}
                options={answerOperators}
                optionsLabel="Operator"
                optionsType="radio"
                setValue={updateOperator}
                value={logicRow.operator}
              />
              :
            </>
          ) : (
            <span>...</span>
          )}
          {logicRow.question?.id && (
            <QuestionLogicOptions
              firstErrorRef={firstErrorRef}
              isCompleted={isCompleted}
              isError={logicRow.hasAnswerError}
              isFocused={logicRow.isFirstAnswerError}
              isRequired
              isSkip
              options={logicRow.question?.answers}
              optionsLabel="Answer Options"
              optionsType="checkbox"
              setValue={updateAnswers}
              value={logicRow.answers}
            />
          )}
        </>
      ) : (
        <>
          <span>&nbsp;is&nbsp;</span>
          <QuestionLogicOptions
            isCompleted={isCompleted}
            options={answerOperators}
            optionsLabel="Operator"
            optionsType="radio"
            setValue={updateOperator}
            value={logicRow.operator}
          />
          <span>&nbsp;:&nbsp;</span>
          <QuestionLogicOptions
            firstErrorRef={firstErrorRef}
            isCompleted={isCompleted}
            isError={!isQuotaGroupSelectionValid() || logicRow.hasAnswerError}
            isFocused={logicRow.isFirstAnswerError}
            isQuotaGroup
            isQuotaGroupSelectionValid={isQuotaGroupSelectionValid()}
            options={quotaGroupOptions}
            optionsLabel="Quota Group"
            optionsType="checkbox"
            setValue={updateQuotaGroups}
            value={quotaGroupOptions.filter(({ id }) =>
              (logicRow.values || []).includes(String(id))
            )}
          />
        </>
      )}
    </div>
  );
};

GroupLogicRow.propTypes = {
  branch: PropTypes.oneOf(QUESTION_GROUP_LOGIC_BRANCHES).isRequired,
  firstErrorRef: PropTypes.object,
  groupId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  index: PropTypes.number.isRequired,
  isCompleted: PropTypes.bool,
  questionIndex: PropTypes.number,
  questionList: PropTypes.array.isRequired,
  questionLogic: PropTypes.object.isRequired,
  setQuestionLogic: PropTypes.func.isRequired,
};

export default GroupLogicRow;
